import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import AppHeader from './components/AppHeader';
import AdminLayout from './components/AdminLayout';

// Eager: landing page (entry point)
import Landing from './pages/Landing';

// Lazy: all other routes
const Login = lazy(() => import('./pages/Login'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const Home = lazy(() => import('./pages/Home'));
const Booking = lazy(() => import('./pages/Booking'));
const Bookings = lazy(() => import('./pages/Bookings'));
const BookingsCalendar = lazy(() => import('./pages/BookingsCalendar'));
const BookingDetail = lazy(() => import('./pages/BookingDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Discounts = lazy(() => import('./pages/Discounts'));
const EditProperty = lazy(() => import('./pages/EditProperty'));
const BookingWidget = lazy(() => import('./components/BookingWidget'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminProperties = lazy(() => import('./pages/admin/AdminProperties'));
const AdminBookings = lazy(() => import('./pages/admin/AdminBookings'));
const AdminWaitlist = lazy(() => import('./pages/admin/AdminWaitlist'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ToastProvider>
          <Router>
            <ScrollToTop />
            <Suspense fallback={null}>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/login" element={<Login />} />
            <Route path="/widget" element={<BookingWidget />} />

            {/* Protected dashboard routes */}
            <Route
              path="/properties"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Home />
                </PrivateRoute>
              }
            />
            <Route
              path="/properties/:id/edit"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <EditProperty />
                </PrivateRoute>
              }
            />
            <Route
              path="/booking"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Booking />
                </PrivateRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Bookings />
                </PrivateRoute>
              }
            />
            <Route
              path="/bookings/calendar"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <BookingsCalendar />
                </PrivateRoute>
              }
            />
            <Route
              path="/bookings/:id"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <BookingDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/discounts"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Discounts />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Profile />
                </PrivateRoute>
              }
            />
            {/* Settings routes (including Stripe Connect return handlers) */}
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/stripe/return"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/stripe/refresh"
              element={
                <PrivateRoute>
                  <AppHeader />
                  <Settings />
                </PrivateRoute>
              }
            />
            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="properties" element={<AdminProperties />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="waitlist" element={<AdminWaitlist />} />
            </Route>
            </Routes>
            </Suspense>
          </Router>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;